import { useAbility, useAuth } from "@cometchat-ai/auth/client";
import { flagEnabled, isHelpdeskOnlyPrincipal } from "@cometchat-ai/auth/shared";
import { Navigate, Outlet, useLocation, useParams } from "react-router";

import type { Action, Subject } from "@cometchat-ai/auth/client";

/**
 * Route-level mirror of the side-nav's visibility rules. The nav merely HIDES
 * entries — every workspace route still mounts if typed as a URL, leaving the
 * page shell to render and fish for 403s. This gate closes that gap for all
 * roles:
 *
 *   1. Desk-only principals (helpdesk-team unit) are pinned to `helpdesk*` —
 *      anything else redirects to the Inbox.
 *   2. Every other section requires the SAME ability tuple its nav item is
 *      gated on; lacking it redirects to the workspace index (which lands on
 *      an ungated page). Sections whose nav item has no ability (workspace,
 *      auth, memory, context-variables) — and the legacy redirect stubs —
 *      stay ungated.
 *
 * Server guards remain the real authority; this is UX so "hidden" and
 * "unreachable" agree. Keep the tuples in lockstep with `NAV_ITEMS` /
 * `SETTINGS_NAV_ITEMS` / `ORG_NAV_ITEMS` / `HELPDESK_NAV_ITEMS` in
 * `components/side-nav.tsx`.
 */
const SECTION_ABILITY: Readonly<Record<string, readonly [Action, Subject]>> = {
  agents: ["read", "Agent"],
  workflows: ["read", "Agent"],
  contacts: ["read", "Agent"],
  channels: ["read", "Agent"],
  agentops: ["read", "Run"],
  conversations: ["read", "Conversation"],
  kb: ["read", "KbDocument"],
  tables: ["read", "DataTable"],
  tools: ["read", "Agent"],
  cards: ["read", "Agent"],
  actions: ["read", "Agent"],
  mcp: ["read", "Agent"],
  handoff: ["read", "HandoffEvent"],
  decisions: ["rule", "Decision"],
  feedback: ["read", "Feedback"],
  workspace: ["read", "Workspace"],
  members: ["read", "Member"],
  roles: ["read", "OrgMember"],
  "feature-flags": ["read", "OrgMember"],
  organization: ["read", "OrgMember"],
};

const HELPDESK_SECTION: Readonly<Record<string, readonly [Action, Subject]>> = {
  members: ["read-members", "Helpdesk"],
  roles: ["manage-roles", "Helpdesk"],
  inbox: ["monitor", "Conversation"],
};

export function RouteAbilityGate() {
  const ability = useAbility();
  const auth = useAuth();
  const { slug } = useParams();
  const location = useLocation();

  const prefix = `/w/${slug}/`;
  const rel = location.pathname.startsWith(prefix) ? location.pathname.slice(prefix.length) : "";
  const [first, second] = rel.split("/");

  // Sections a desk-only principal may visit: the helpdesk subtree plus the
  // Feedback surface (its nav item lives in the Helpdesk group).
  if (isHelpdeskOnlyPrincipal(auth.permissions) && first !== "helpdesk" && first !== "feedback") {
    return <Navigate to={`/w/${slug}/helpdesk`} replace />;
  }

  // Workflows surface is flag-gated (`surface.workflows`, default OFF). When
  // off, a typed `/workflows` URL redirects to the workspace index — mirrors
  // the hidden nav item so hidden and unreachable agree.
  if (first === "workflows" && !flagEnabled(auth, "surface.workflows")) {
    return <Navigate to={`/w/${slug}`} replace />;
  }

  const required =
    first === "helpdesk"
      ? HELPDESK_SECTION[second === "members" || second === "roles" ? second : "inbox"]
      : first
        ? SECTION_ABILITY[first]
        : undefined;
  if (required && !ability.can(...required)) {
    return <Navigate to={`/w/${slug}`} replace />;
  }
  return <Outlet />;
}
